import type { ReactNode } from "react";
import { cn } from "../../lib/cn";
import { Segmented, type SegmentedOption } from "../../primitives/segmented";

/**
 * Onboarding pulse question. Composition:
 *
 *   header
 *     mono index + id (optional)             motif (right-side svg)
 *   serif question
 *   context paragraph (optional)
 *   likert / segmented row
 *   ack line (changes per selected value, fades in)
 *
 * The motif is an arbitrary ReactNode keyed off the selected value -
 * callers pass a render-prop so they can swap an illustration per
 * answer (plates / clouds / steps / etc.). The ack lookup is a string
 * array indexed by likert value (1-5).
 *
 * Distinct from `Segmented` alone: PulseQuestion owns the editorial
 * frame, ack feedback, and per-question motif slot.
 */
export interface PulseQuestionProps {
  eyebrow?: ReactNode;
  question: ReactNode;
  context?: ReactNode;
  /** Optional 2-digit step index ("01", "02", …). */
  index?: ReactNode;
  /** Optional short id ("food", "sleep") rendered next to the index. */
  pillarId?: ReactNode;
  /** Motif slot - takes the currently-selected numeric value (1-5) or
   *  null and returns a ReactNode. Used for the per-answer
   *  illustration. */
  renderMotif?: (value: number | null) => ReactNode;
  /** Ack lookup - rendered below the segmented when a value is set. */
  ackByValue?: ReadonlyArray<string>;
  options?: ReadonlyArray<SegmentedOption<string>>;
  value: string | null;
  onChange?: (next: string) => void;
  className?: string;
  /**
   * Whether to wrap the question in the canonical card chrome (border, fill,
   * shadow, padding, answered-state promotion). Default true. Set false when
   * the caller already owns the frame, e.g. a one-at-a-time sub-stepper that
   * shows a single question with no surrounding list.
   */
  framed?: boolean;
}

const DEFAULT_OPTIONS: ReadonlyArray<SegmentedOption<string>> = [
  { value: "not", label: "Not at all", index: "01" },
  { value: "rarely", label: "Rarely", index: "02" },
  { value: "sometimes", label: "Sometimes", index: "03" },
  { value: "often", label: "Often", index: "04" },
  { value: "most", label: "Most days", index: "05" },
];

export function PulseQuestion({
  eyebrow,
  question,
  context,
  index,
  pillarId,
  renderMotif,
  ackByValue,
  options = DEFAULT_OPTIONS,
  value,
  onChange,
  className,
  framed = true,
}: PulseQuestionProps) {
  // Map the option value back to its 1-5 numeric index for motif + ack.
  const numericValue = value
    ? Math.max(
        0,
        options.findIndex((o) => o.value === value),
      ) + 1 || null
    : null;
  const ackText =
    numericValue && ackByValue && numericValue >= 1 && numericValue <= ackByValue.length
      ? (ackByValue[numericValue - 1] ?? "")
      : "";
  const answered = value !== null;
  const motif = renderMotif ? (
    // The motif SVGs (PlatesMotif, StepsMotif, MoonMotif, ...) are
    // viewBox-driven with `preserveAspectRatio`, so they need an explicit
    // sized parent to paint into.
    <div
      className="flex h-9 w-[140px] shrink-0 items-center justify-end [&_svg]:block [&_svg]:h-full [&_svg]:w-full"
      aria-hidden="true"
    >
      {renderMotif(numericValue)}
    </div>
  ) : null;
  const headerMotif = motif;
  const showHeader = Boolean(index || pillarId || eyebrow || headerMotif);
  return (
    <section
      className={cn(
        "flex flex-col gap-4",
        // Canonical card chrome: rounded-2xl, mist-dark/40 border, white fill,
        // shadow-card with hover lift. Answered state promotes the border to
        // 2px teal-600 + teal-50 tint so the card reads as 'done'. Skipped
        // entirely when `framed` is false (the caller owns the frame).
        framed &&
          "rounded-2xl border bg-surface-elevated shadow-card transition-all duration-200 hover:shadow-card-hover",
        framed &&
          (answered
            ? "border-2 border-teal-600 bg-surface-secondary/40 p-[19px]"
            : "border-border-card p-5"),
        className,
      )}
    >
      {showHeader ? (
        <header className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-1">
            {(index || pillarId) && (
              <div className="flex items-baseline gap-2 font-mono text-xs font-semibold uppercase tracking-[0.16em]">
                {index ? <span className="text-ink-brand">{index}</span> : null}
                {pillarId ? <span className="text-ink-tertiary">{pillarId}</span> : null}
              </div>
            )}
            {eyebrow ? (
              <div className="font-mono text-xs font-semibold uppercase tracking-[0.18em] text-ink-eyebrow">
                {eyebrow}
              </div>
            ) : null}
          </div>
          {headerMotif}
        </header>
      ) : null}
      <div className="flex items-center justify-between gap-6">
        <h2 className="m-0 max-w-[36ch] font-title text-[22px] font-medium leading-snug tracking-tight text-ink-primary">
          {question}
        </h2>
      </div>
      {context ? (
        <p className="m-0 max-w-[43ch] text-sm leading-relaxed text-ink-secondary">{context}</p>
      ) : null}
      <Segmented options={options} value={value} onChange={onChange} />
      {ackText ? (
        <div
          aria-live="polite"
          className="font-sans text-sm italic leading-relaxed text-teal-800 transition-opacity duration-200"
        >
          {ackText}
        </div>
      ) : null}
    </section>
  );
}
